<template>
  <div>
    <div class="mfg-header">
      <h4 class="mfg-title">Manufacturing Settings</h4>
    </div>

    <!-- Company policy -->
    <div class="mfg-box mb-3">
      <div class="card-body">
        <h6 class="section-title">Costing &amp; controls</h6>
        <div class="form-row">
          <div class="col-md-6 form-group">
            <label class="small text-secondary mb-1">Inventory costing method</label>
            <select class="custom-select" v-model="company.inventory_costing_method">
              <option value="last_cost">Last cost (current default)</option>
              <option value="weighted_average">Weighted average</option>
            </select>
            <small class="text-muted">Applies from the next receipt forward; past costs are never restated. Confirm the method with your accountant.</small>
          </div>
          <div class="col-md-6 form-group">
            <label class="small text-secondary mb-1">Segregation of duties</label>
            <div class="custom-control custom-switch mt-1">
              <input type="checkbox" class="custom-control-input" id="sod" v-model="company.manufacturing_segregation_of_duties" />
              <label class="custom-control-label" for="sod">Recipes and production orders must be approved by a different person</label>
            </div>
          </div>
        </div>
        <button class="btn btn-primary" :disabled="busy" @click="saveCompany">Save</button>
        <span v-if="company.stock_ledger_started_at" class="small text-muted ml-2">Stock ledger running since {{ company.stock_ledger_started_at.substring(0, 10) }}</span>
      </div>
    </div>

    <!-- Quality tests -->
    <div class="mfg-box mb-3">
      <div class="card-body">
        <h6 class="section-title">Quality tests</h6>
        <table class="table table-sm">
          <thead><tr><th>Test</th><th>Unit</th><th>Min</th><th>Max</th><th>Applies to</th></tr></thead>
          <tbody>
            <tr v-for="t in tests" :key="t.id"><td>{{ t.name }}</td><td>{{ t.unit || '—' }}</td><td>{{ t.min_value == null ? '—' : t.min_value }}</td><td>{{ t.max_value == null ? '—' : t.max_value }}</td><td>{{ t.applies_to.replace('_', ' ') }}</td></tr>
            <tr v-if="!tests.length"><td colspan="5" class="text-muted">None yet — e.g. Moisture (%) max 13.5, Aflatoxin B1 (ppb) max 10.</td></tr>
          </tbody>
        </table>
        <form class="form-row align-items-end" @submit.prevent="saveTest">
          <div class="col-md-3 mb-2"><label class="small text-secondary mb-1">Name</label><input class="form-control" v-model="test.name" /></div>
          <div class="col-md-2 mb-2"><label class="small text-secondary mb-1">Unit</label><input class="form-control" v-model="test.unit" /></div>
          <div class="col-md-2 mb-2"><label class="small text-secondary mb-1">Min</label><input type="number" step="any" class="form-control" v-model.number="test.min_value" /></div>
          <div class="col-md-2 mb-2"><label class="small text-secondary mb-1">Max</label><input type="number" step="any" class="form-control" v-model.number="test.max_value" /></div>
          <div class="col-md-2 mb-2"><label class="small text-secondary mb-1">Applies to</label>
            <select class="custom-select" v-model="test.applies_to"><option value="any">Any</option><option value="raw_material">Raw material</option><option value="finished_good">Finished good</option></select></div>
          <div class="col-md-1 mb-2"><button class="btn btn-primary btn-block" :disabled="!test.name">Add</button></div>
        </form>
      </div>
    </div>

    <!-- Product units & lots -->
    <div class="mfg-box mb-3">
      <div class="card-body">
        <h6 class="section-title">Product units &amp; lot tracking</h6>
        <div class="filter-bar">
          <input class="form-control" v-model="productSearch" @input="debouncedSearch" placeholder="Find a product" style="flex:1" />
        </div>
        <div v-if="products.length && !product" class="list-group mb-2">
          <button v-for="p in products" :key="p.id" class="list-group-item list-group-item-action" @click="selectProduct(p)">{{ p.product }}</button>
        </div>

        <div v-if="product">
          <div class="d-flex align-items-center mb-2">
            <h6 class="mb-0">{{ product.product }}</h6>
            <button class="btn btn-sm btn-link ml-auto" @click="product = null">Change product</button>
          </div>
          <div class="row mb-4 pb-2 mfg-kpis">
            <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Stock unit (base)</div><div class="font-weight-bold">{{ product.base_unit }}</div>
              <div v-if="!product.base_unit_recognised" class="small text-danger">Not a standard unit — only explicit conversions will work.</div></div>
            <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">On hand</div><div class="font-weight-bold">{{ qty(product.remaining_units) }} {{ product.base_unit }}</div></div>
            <div class="col-6 col-md-3 kpi-col"><div class="text-secondary small">Cost per {{ product.base_unit }}</div><div class="font-weight-bold">{{ product.unit_cost == null ? 'Not set' : money(product.unit_cost) }}</div></div>
          </div>

          <div class="form-row">
            <div class="col-md-4 form-group" v-if="!product.base_unit_explicit">
              <label class="small text-secondary mb-1">Set stock unit</label>
              <div class="input-group">
                <select class="custom-select" v-model="baseUnit"><option v-for="(u, code) in catalogue" :key="code" :value="code">{{ u.label }} ({{ code }})</option></select>
                <div class="input-group-append"><button class="btn btn-secondary" @click="saveProduct({ base_unit: baseUnit })">Set</button></div>
              </div>
              <small class="text-muted">Only possible while the product has no stock history.</small>
            </div>
            <div class="col-md-4 form-group">
              <div class="custom-control custom-switch mt-4">
                <input type="checkbox" class="custom-control-input" id="trackLots" :checked="product.track_lots" :disabled="product.track_lots" @change="saveProduct({ track_lots: true })" />
                <label class="custom-control-label" for="trackLots">Track lots / batches</label>
              </div>
            </div>
            <div class="col-md-4 form-group">
              <div class="custom-control custom-switch mt-4">
                <input type="checkbox" class="custom-control-input" id="requiresQc" :checked="product.requires_qc" @change="saveProduct({ requires_qc: !product.requires_qc })" />
                <label class="custom-control-label" for="requiresQc">Hold new lots for quality release</label>
              </div>
            </div>
          </div>

          <table class="table table-sm">
            <thead><tr><th>Unit</th><th class="text-right">= {{ product.base_unit }}</th></tr></thead>
            <tbody><tr v-for="u in product.units" :key="u.unit"><td>{{ u.unit }} <span v-if="u.is_base" class="badge badge-secondary">base</span></td><td class="text-right">{{ qty(u.factor_to_base) }}</td></tr></tbody>
          </table>
          <form class="form-row align-items-end" @submit.prevent="addUnit">
            <div class="col-md-4 mb-2"><label class="small text-secondary mb-1">New unit code (e.g. bag50)</label><input class="form-control" v-model="newUnit.unit" /></div>
            <div class="col-md-4 mb-2"><label class="small text-secondary mb-1">1 unit = how many {{ product.base_unit }}</label><input type="number" step="any" min="0" class="form-control" v-model.number="newUnit.factor_to_base" /></div>
            <div class="col-md-4 mb-2"><button class="btn btn-primary btn-block" :disabled="!newUnit.unit || !(newUnit.factor_to_base > 0)">Add unit</button></div>
          </form>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>
  </div>
</template>

<script>
import { apiError, fmtMoney, fmtQty } from './mfgHelpers';

export default {
  name: 'InventorySettingsPanel',
  data() {
    return {
      company: {}, catalogue: {}, tests: [], busy: false, error: null,
      test: { name: '', unit: '', min_value: null, max_value: null, applies_to: 'any' },
      productSearch: '', products: [], product: null, baseUnit: 'kg', newUnit: { unit: '', factor_to_base: null }, timer: null,
    };
  },
  mounted() {
    axios.get('/api/manufacturing/settings').then(({ data }) => { this.company = data; this.catalogue = data.units; });
    this.loadTests();
  },
  methods: {
    qty: fmtQty, money: fmtMoney,
    async loadTests() { this.tests = (await axios.get('/api/manufacturing/quality-tests')).data; },
    async saveCompany() {
      this.busy = true; this.error = null;
      try {
        this.company = (await axios.patch('/api/manufacturing/settings', {
          inventory_costing_method: this.company.inventory_costing_method,
          manufacturing_segregation_of_duties: this.company.manufacturing_segregation_of_duties,
        })).data;
        this.$toasts && this.$toasts.push({ type: 'success', message: 'Settings saved.' });
      } catch (e) { this.error = apiError(e); } finally { this.busy = false; }
    },
    async saveTest() {
      this.error = null;
      try {
        await axios.post('/api/manufacturing/quality-tests', this.test);
        this.test = { name: '', unit: '', min_value: null, max_value: null, applies_to: 'any' };
        this.loadTests();
      } catch (e) { this.error = apiError(e); }
    },
    debouncedSearch() {
      clearTimeout(this.timer);
      this.timer = setTimeout(async () => {
        if (this.productSearch.length < 2) { this.products = []; return; }
        const { data } = await axios.get('/api/products', { params: { search: this.productSearch, per_page: 10 } });
        this.products = data.data || data;
      }, 350);
    },
    async selectProduct(p) {
      this.error = null;
      this.product = (await axios.get(`/api/manufacturing/products/${p.id}/inventory-settings`)).data;
    },
    async saveProduct(payload) {
      this.error = null;
      try {
        this.product = (await axios.patch(`/api/manufacturing/products/${this.product.id}/inventory-settings`, payload)).data;
      } catch (e) { this.error = apiError(e); this.selectProduct(this.product); }
    },
    async addUnit() {
      this.error = null;
      try {
        this.product = (await axios.post(`/api/manufacturing/products/${this.product.id}/units`, this.newUnit)).data;
        this.newUnit = { unit: '', factor_to_base: null };
      } catch (e) { this.error = apiError(e); }
    },
  },
};
</script>
